import { AssignmentDocumentDTO, AssignmentDocumentStatusHistoryDTO } from 'models/assignment'
import { EntityKeys, QueryType, getMutation } from 'utils/reactQuery'

import { Auth0Roles } from 'models/auth'
import { AxiosResponse } from 'axios'
import { DocumentUploadType } from 'constants/documents'
import { useAPI } from 'utils/API'
import { useQuery } from '@tanstack/react-query'

export enum DocumentQueryKeys {
  STATUS_HISTORY = 'STATUS_HISTORY',
}

enum Endpoints {
  GET_ASSIGNMENT_DOCUMENTS_WITH_WORKSPACE = '/workspace/{workspaceId}/assignment/{assignmentId}/documents?type={type}',
  GET_ASSIGNMENT_DOCUMENTS = '/assignment/{assignmentId}/documents?type={type}',
  GET_ASSIGNMENT_DOCUMENT_FILE_WITH_WORKSPACE = '/workspace/{workspaceId}/assignment/{assignmentId}/documents/{filename}',
  GET_ASSIGNMENT_DOCUMENT_FILE = '/assignment/{assignmentId}/documents/{filename}',
  ACCEPT_DOCUMENT = '/workspace/{workspaceId}/assignment/{assignmentId}/documents/accept',
  ADMIN_SEND_ASSIGNMENT_DOCUMENTS_TO_CLIENT = '/assignment/{assignmentId}/documents/submit',
  GET_DOCUMENT_STATUS_HISTORY = '/workspace/{workspaceId}/assignment/{assignmentId}/documents/status-history',
}

// QUERIES
export function useGetAssignmentDocuments(assignmentId: string, type: DocumentUploadType, workspaceId: string, role: Auth0Roles) {
  const api = useAPI<Endpoints>()

  return useQuery({
    queryKey: [EntityKeys.ASSIGNMENT, QueryType.GET, { assignmentId }],
    queryFn: () => api.get<AssignmentDocumentDTO[]>(
      // Only clients own a workspace. Admins and creatives resolve workspaceId to '' and the workspace-scoped
      // route then reads /workspace//assignment/... which the BE rejects (400), leaving the document list empty.
      role.isClient ? Endpoints.GET_ASSIGNMENT_DOCUMENTS_WITH_WORKSPACE : Endpoints.GET_ASSIGNMENT_DOCUMENTS,
      {
        assignmentId,
        type,
        workspaceId
      },
      true
    ),
    enabled: !!assignmentId,
  })
}

// QUERIES
export function useGetAssignmentDocumentsStatusHistory(assignmentId: string, workspaceId: string) {
  const api = useAPI<Endpoints>()

  return useQuery({
    queryKey: [EntityKeys.ASSIGNMENT, QueryType.LIST, DocumentQueryKeys.STATUS_HISTORY, { assignmentId }],
    queryFn: () => api.get<AssignmentDocumentStatusHistoryDTO[]>(
      Endpoints.GET_DOCUMENT_STATUS_HISTORY,
      {
        assignmentId,
        workspaceId
      },
      true
    ),
    enabled: !!assignmentId,
  })
}

// MUTATIONS

export function useGetAssignmentDocumentFile() {
  const api = useAPI<Endpoints>()

  return getMutation<AxiosResponse<AssignmentDocumentDTO>, { assignmentId: string, filename: string, workspaceId: string, isClient: boolean }>(
    ({ assignmentId, filename, workspaceId, isClient }) => api.get(
      isClient ? Endpoints.GET_ASSIGNMENT_DOCUMENT_FILE_WITH_WORKSPACE : Endpoints.GET_ASSIGNMENT_DOCUMENT_FILE,
      { assignmentId, filename, workspaceId },
      true,
      {
        params: { type: DocumentUploadType.INPUT }
      }
    )
  )
}

export function useAcceptDocument() {
  const api = useAPI<Endpoints>()

  return getMutation<{}, { assignmentId: string, workspaceId: string }>(
    ({ assignmentId, workspaceId }) => api.post(
      Endpoints.ACCEPT_DOCUMENT,
      {
        assignmentId: assignmentId.toString(),
        workspaceId
      },
      {},
      false
    ),
    (client, { assignmentId }) => {
      client.invalidateQueries({ queryKey: [EntityKeys.ASSIGNMENT, QueryType.GET_ONE, { assignmentId }] })
    }
  )
}

/** Mutation for send assignment documents to the client */
export function useSendDocumentsToClient() {
  const api = useAPI<Endpoints>()

  return getMutation<{}, { assignmentId: string }>(
    ({ assignmentId }) => api.post(
      Endpoints.ADMIN_SEND_ASSIGNMENT_DOCUMENTS_TO_CLIENT,
      { assignmentId },
      {},
      false
    )
  )
}
